import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap, finalize } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class GatewayConfigService {
  private useGwEndpointsSubject = new BehaviorSubject<boolean>(false);
  public useGwEndpoints$ = this.useGwEndpointsSubject.asObservable();

  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private paynowPendingMessageSubject = new BehaviorSubject<null|string>(null)
  private cashDrawPendingMessageSubject = new BehaviorSubject<null|string>(null);
  public cashDrawPendingMessage$ = this.cashDrawPendingMessageSubject.asObservable();
  public paynowPendingMessage$ = this.paynowPendingMessageSubject.asObservable();



  private errorSubject = new BehaviorSubject<any>(null);
  public error$ = this.errorSubject.asObservable();

  private refetchTriggeredSubject = new BehaviorSubject<boolean>(false);
  public refetchTriggered$ = this.refetchTriggeredSubject.asObservable();

  private configurationLoaded = false;
  private configurationPromise: Promise<boolean | undefined>;

  constructor(private http: HttpClient) {
    // Initialize with default promise that resolves to false
    console.log('GatewayConfigService - Constructor called');
    this.configurationPromise = Promise.resolve(false);
  }

  /**
   * Initialize the gateway configuration
   * This should be called after authentication is complete
   */
  initialize(): void {
    console.log('GatewayConfigService - initialize() called');

    // Check if already initialized
    if (this.configurationLoaded) {
      console.log('GatewayConfigService - Already initialized');
      return;
    }

    // Check if we have a token before making the call
    const tokenStr = window.localStorage.getItem("token");
    if (!tokenStr) {
      console.warn('GatewayConfigService - No token available, defaulting to false');
      this.configurationLoaded = true;
      this.useGwEndpointsSubject.next(false);
      this.configurationPromise = Promise.resolve(false);
      return;
    }

    // Start fetching configuration
    console.log('GatewayConfigService - Token available, fetching configuration...');
    this.configurationPromise = this.fetchGatewayConfiguration()
      .toPromise()
      .then(result => {
        console.log('GatewayConfigService - Configuration loaded successfully, result:', result);
        return result || false;
      })
      .catch(error => {
        console.error('GatewayConfigService - Configuration failed:', error);
        // Mark as loaded even on error to prevent hanging
        this.configurationLoaded = true;
        this.useGwEndpointsSubject.next(false);
        return false;
      });
  }

  /**
   * Get the current value of use_gw_endpoints
   */
  get useGwEndpoints(): boolean {
    const value = this.useGwEndpointsSubject.value;
    console.log('GatewayConfigService - useGwEndpoints getter called, returning:', value);
    return value;
  }

  /**
   * Get the proper endpoint based on configuration (synchronous)
   * This should be used for immediate API calls
   */
  getApiEndpoint(baseEndpoint: string, oppId: string | null): string {
    const pathSegment = oppId ? `/${oppId}` : '';

    // If configuration hasn't loaded yet, use default (non-gateway) endpoints
    if (!this.configurationLoaded) {
      console.log(`GatewayConfigService - Configuration not loaded yet for ${baseEndpoint}, using default endpoint`);
      return `${environment.baseUrl}v1/revenued/${baseEndpoint}${pathSegment}`;
    }

    const useGw = this.useGwEndpointsSubject.value;
    console.log(`GatewayConfigService - getApiEndpoint called for ${baseEndpoint}, useGw:`, useGw);

    if (useGw) {
      const gwEndpoint = `${environment.baseUrl}v1/gw/${baseEndpoint}${pathSegment}`;
      console.log(`GatewayConfigService - Using gateway endpoint:`, gwEndpoint);
      return gwEndpoint;
    }

    const defaultEndpoint = `${environment.baseUrl}v1/revenued/${baseEndpoint}${pathSegment}`;
    console.log(`GatewayConfigService - Using default endpoint:`, defaultEndpoint);
    return defaultEndpoint;
  }

  /**
   * Fetch gateway configuration from getSiteMetaData endpoint
   */
  fetchGatewayConfiguration(): Observable<boolean> {
    this.loadingSubject.next(true);
    this.errorSubject.next(null);

    // Check if we have the necessary environment variables
    console.log('GatewayConfigService - Environment check:', {
      baseUrl: environment.baseUrl,
      appName: environment.appName,
      middlewareToken: environment.REVENUED_MIDDLEWARE_TOKEN
    });

    // Get the token if available
    let headers = new HttpHeaders()
      .append('Accept', 'application/json, text/plain, */*')
      .append('Access-Control-Allow-Headers', 'Access-Control-Allow-Headers, Access-Control-Allow-Origin, Access-Control-Request-Method')
      .append('REVENUED_MIDDLEWARE_TOKEN', environment.REVENUED_MIDDLEWARE_TOKEN)
      .append('appname', environment.appName)
      .append('skip_interceptor', 'true')
      .append('skip_interceptor_rum_logs', 'true');

    // Add authorization header if token is available
    const tokenStr = window.localStorage.getItem("token");
    if (tokenStr) {
      try {
        const token = JSON.parse(tokenStr);
        if (token.id_token) {
          headers = headers.append('Authorization', `Bearer ${token.id_token}`);
        }
      } catch (e) {
        console.error('GatewayConfigService - Error parsing token:', e);
      }
    }

    const url = `${environment.baseUrl}v1/revenued/getSiteMetaData`;
    console.log('GatewayConfigService - Making request to:', url);

    return this.http.get<any>(url, { headers }).pipe(
      tap(response => {
        console.log('Gateway configuration response:', response);
        const previousValue = this.useGwEndpointsSubject.value;
        const useGwEndpoints = response?.use_gw_endpoints === true || response?.use_gw_endpoints === 'true';
        const gwCashDrawStatusPendingMessage = useGwEndpoints ? response?.gw_cash_draw_status_pending_message : null;
        this.cashDrawPendingMessageSubject.next(gwCashDrawStatusPendingMessage)
        const gwpaynowPendingMessage = useGwEndpoints ? response?.gw_prepay_status_pending_message : null;
        this.paynowPendingMessageSubject.next(gwpaynowPendingMessage)
        console.log('Setting useGwEndpoints to:', useGwEndpoints, 'previous value was:', previousValue);

        // Only emit if the value actually changed or if this is the first load
        if (!this.configurationLoaded || previousValue !== useGwEndpoints) {
          this.useGwEndpointsSubject.next(useGwEndpoints);
          // this.cashDrawPendingMessageSubject.next(gwCashDrawStatusPendingMessage)

          // If configuration just loaded and useGw is true, or changed to true, trigger refetch
          if (useGwEndpoints && (!this.configurationLoaded || previousValue === false)) {
            console.log('GatewayConfigService - Triggering refetch due to useGwEndpoints = true');
            this.refetchTriggeredSubject.next(true);
          }
        }

        this.configurationLoaded = true;
        this.loadingSubject.next(false);
      }),
      finalize(() => {
        console.log('Gateway configuration request completed');
      }),
      map(response => response?.use_gw_endpoints === true || response?.use_gw_endpoints === 'true'),
      catchError(error => {
        console.error('Error fetching gateway configuration:', error);
        this.errorSubject.next(error);
        this.loadingSubject.next(false);
        this.configurationLoaded = true;
        // Default to false on error
        this.useGwEndpointsSubject.next(false);
        return of(false);
      })
    );
  }

  /**
   * Transform an API endpoint based on gateway configuration
   * @param endpoint The original endpoint
   * @returns The transformed endpoint with /gw/ prefix if needed
   */
  transformEndpoint(endpoint: string): string {
    if (!this.useGwEndpoints) {
      return endpoint;
    }

    // List of endpoints that should be prefixed with /gw/
    const gatewayEndpoints = [
      'accountsummary',
      'getPreDrawEstimatesForOverLimit',
      'requestDraw',
      'postCashDrawReject'
    ];

    // Check if the endpoint should be transformed
    const shouldTransform = gatewayEndpoints.some(gwEndpoint =>
      endpoint.includes(gwEndpoint)
    );

    if (shouldTransform) {
      // Find where the version part ends (e.g., /v1/revenued/)
      const versionMatch = endpoint.match(/(\/v\d+\/\w+\/)/);
      if (versionMatch) {
        const versionPart = versionMatch[1];
        const endpointPart = endpoint.substring(versionPart.length);
        return versionPart + 'gw/' + endpointPart;
      }
    }

    return endpoint;
  }

  /**
   * Refresh gateway configuration
   */
  refreshConfiguration(): Observable<boolean> {
    this.configurationPromise = this.fetchGatewayConfiguration().toPromise();
    return this.fetchGatewayConfiguration();
  }

  /**
   * Wait for configuration to be loaded
   */
  async waitForConfiguration(): Promise<boolean> {
    if (this.configurationLoaded) {
      return this.useGwEndpoints;
    }
    const result = await this.configurationPromise;
    return result || false;
  }

  /**
   * Returns a promise that resolves when configuration is ready
   * Useful for ensuring configuration is loaded before making API calls
   */
  ensureConfigurationLoaded(): Promise<void> {
    if (this.configurationLoaded) {
      console.log('GatewayConfigService - Configuration already loaded');
      return Promise.resolve();
    }

    console.log('GatewayConfigService - Waiting for configuration to load...');
    return new Promise((resolve, reject) => {
      let resolved = false;

      // Set a timeout to prevent hanging
      const timeout = setTimeout(() => {
        if (!resolved) {
          resolved = true;
          console.error('GatewayConfigService - Configuration loading timeout');
          reject(new Error('Configuration loading timeout'));
        }
      }, 5000); // 5 second timeout

      // First check immediately if already loaded
      if (this.configurationLoaded && !resolved) {
        resolved = true;
        console.log('GatewayConfigService - Configuration already loaded on subscription');
        clearTimeout(timeout);
        resolve();
        return;
      }

      const subscription = this.useGwEndpoints$.subscribe(value => {
        if (this.configurationLoaded && !resolved) {
          resolved = true;
          console.log('GatewayConfigService - Configuration loaded, proceeding');
          clearTimeout(timeout);
          subscription.unsubscribe();
          resolve();
        }
      });

      // Also check if configuration fails
      const errorSubscription = this.error$.subscribe(error => {
        if (error && !resolved) {
          resolved = true;
          console.error('GatewayConfigService - Configuration error:', error);
          clearTimeout(timeout);
          subscription.unsubscribe();
          errorSubscription.unsubscribe();
          resolve(); // Resolve anyway to not block the app
        }
      });
    });
  }

  /**
   * Get endpoint with proper gateway prefix (async version)
   */
  async getEndpoint(baseEndpoint: string, oppId: string): Promise<string> {
    await this.waitForConfiguration();
    const useGw = this.useGwEndpoints;

    if (!useGw) {
      return `${environment.baseUrl}v1/revenued/${baseEndpoint}/${oppId}`;
    }

    return `${environment.baseUrl}v1/gw/${baseEndpoint}/${oppId}`;
  }

  /**
   * Manually trigger a refetch event
   * This can be used by components to force a data refresh
   */
  triggerRefetch(): void {
    console.log('GatewayConfigService - Manual refetch triggered');
    this.refetchTriggeredSubject.next(true);
  }
}
